import { Box, makeStyles } from '@material-ui/core'
import useCheckPermission from '../../../../utilities/checkPermission'
import { formatThaiDate } from './messenger.helper'
import { getFileIconPath } from '../../../../utilities/pathImg'
import type { Messenger, SenderRole } from '../../../../services/web/useMessenger'
import useTranslate from '../../../../i18n/useTranslate'
import { useMemo } from 'react'

export interface MessengerItemProps {
    messenger: Messenger
}

const MessengerItem = (props: MessengerItemProps) => {
    const { messenger } = props
    const classes = useStyles()
    const translate = useTranslate()
    const checkPermission = useCheckPermission()
    const senderRole: SenderRole = checkPermission.isUserResearcher() ? 'researcher' : 'officer'
    const fileInfo = messenger.documentInfo?.[0]?.fileInfo?.[0] ?? undefined
    const currentLanguage = translate?.languageStore?.currentLanguage === 'en' ? 'en' : 'th'

    const getMessengerItemClasses = (itemSenderRole: SenderRole) => {
        return senderRole === itemSenderRole ? 'user-item' : 'other-item'
    }

    const getSenderText = (itemSenderRole: SenderRole) => {
        return itemSenderRole === 'researcher'
            ? translate.get('UM.MESSAGE.RESEARCHER')
            : translate.get('UM.MESSAGE.OFFICER')
    }

    const formattedFileDate = useMemo(
        () => formatThaiDate(fileInfo?.date, currentLanguage),
        [fileInfo?.date, currentLanguage],
    )

    const formattedCreatedDate = useMemo(
        () => formatThaiDate(messenger?.createdDate, currentLanguage),
        [messenger?.createdDate, currentLanguage],
    )

    return (
        <Box className={`${classes.messengerItem} ${getMessengerItemClasses(messenger.senderRole)}`}>
            <Box className="message-text">{messenger.messageText}</Box>
            {fileInfo ? (
                <Box className="document-info">
                    <img src={process.env.PUBLIC_URL + getFileIconPath(fileInfo.filename)} alt="file icon" />
                    <a href={fileInfo.path} target="_blank" rel="noreferrer">
                        {`${fileInfo.filename} ${translate.get('UM.MESSAGE.LATEST_DATE')} ${formattedFileDate}`}
                    </a>
                </Box>
            ) : null}
            <Box className="sender-info">{`${getSenderText(messenger.senderRole)} ${formattedCreatedDate}`}</Box>
        </Box>
    )
}

const useStyles = makeStyles((theme) => ({
    messengerItem: {
        borderRadius: 8,
        paddingInline: 16,
        paddingTop: 20,
        paddingBottom: 8,
        maxWidth: 'min(540px, 100%)',

        '&.user-item': {
            color: theme.palette.common.white,
            backgroundColor: theme.palette.primary.main,
            alignSelf: 'flex-end',
            border: '1px solid #DB5F8E',

            '& .sender-info': {
                textAlign: 'right',
                marginTop: 4,
                color: 'rgba(213, 213, 213, 1)',
            },
        },

        '&.other-item': {
            color: theme.palette.common.black,
            backgroundColor: 'rgba(219, 95, 142, 0.15)',
            alignSelf: 'flex-start',
            border: '1px solid rgba(220, 220, 220, 1)',

            '& .sender-info': {
                marginTop: 4,
                color: 'rgba(185, 185, 185, 1)',
            },
        },

        '& .message-text': {
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            margin: 0,
        },

        '& .document-info': {
            display: 'flex',
            gap: 8,
            padding: 12,
            color: theme.palette.common.black,
            backgroundColor: theme.palette.common.white,
            border: '1px solid rgba(220, 220, 220, 1)',
            borderRadius: 4,
            width: 540,
            marginTop: 4,
            maxWidth: '100%',

            '& img': {
                flexShrink: 0,
            },

            '& a': {
                color: theme.palette.common.black,
                textDecoration: 'none',
                flexGrow: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',

                '&:hover': {
                    textDecoration: 'underline',
                },
            },
        },
    },
}))

export default MessengerItem
